import type { ScorecardTemplateFormValues } from '@kula-ai/components';

export interface ScorecardModelResult {
  hasValidData: boolean;
  payload: Record<string, unknown>;
}

/**
 * Build the `/ats_job_stage_actions` scorecard payload from the builder's
 * form values. Extracted from the `Scorecard` container so the wire contract
 * (the part that must stay byte-identical post kula-ai migration) is pure and
 * unit-testable.
 *
 * Two load-bearing details preserved from the legacy Formik builder:
 *  - options collapse from `[{ optionText }]` back to `string[]`
 *  - `scorecard_required` ships as a STRING ('true' | 'false'), never a JSON
 *    boolean — the BE coerces a boolean `false` to `true` (ENG-3207).
 */
export const buildScorecardModel = (
  scorecardRequired: boolean,
  isValid: boolean,
  values?: Partial<ScorecardTemplateFormValues>
): ScorecardModelResult => {
  if (!scorecardRequired) {
    return {
      hasValidData: true,
      payload: { scorecard_required: 'false' },
    };
  }

  const {
    feedback_description,
    is_feedback_mandatory,
    rating_description,
    scoreCards = [],
  } = values ?? {};

  const questions = scoreCards.map((scorecard) => ({
    question: scorecard?.question,
    type: scorecard?.type,
    mandatory: scorecard?.mandatory || false,
    description: scorecard?.description || '',
    ...(scorecard?.options?.length
      ? { options: scorecard.options.map((option) => option?.optionText) }
      : {}),
  }));

  return {
    hasValidData: isValid,
    payload: {
      scorecard_config: {
        questions,
        is_feedback_mandatory,
      },
      scorecard_feedback_description: feedback_description,
      scorecard_rating_description: rating_description,
      scorecard_required: 'true',
    },
  };
};
